import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SlotWithSegments as Slot, SlotSegment } from "@/types/slot";
import { slotService } from "@/services/slotService";
import { format, parseISO } from "date-fns";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { SlotBookingsList } from "./SlotBookingsList";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "@/components/ui/use-toast";
import { Calendar, Clock } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";

interface SlotDetailProps {
  slot: Slot;
  onUpdate: () => void;
}

export function SlotDetail({ slot, onUpdate }: SlotDetailProps) {
  const [activeTab, setActiveTab] = useState("details");
  const [isDeleting, setIsDeleting] = useState(false);
  const [editableName, setEditableName] = useState(slot.name || "");
  const [editableCapacity, setEditableCapacity] = useState(slot.capacity);
  const [isDirty, setIsDirty] = useState(false);

  const availableSeats = slot.capacity - slot.filled_seats;
  const segments = slot.segments || [];

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditableName(e.target.value);
    setIsDirty(true);
  };

  const handleCapacityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = parseInt(e.target.value);
    // Only allow capacity to increase, and ensure it's not less than filled seats
    if (newValue >= slot.capacity && newValue >= slot.filled_seats) {
      setEditableCapacity(newValue);
      setIsDirty(true);
    }
  };

  const handleSave = async () => {
    try {
      await slotService.updateSlot(slot.id, {
        name: editableName,
        capacity: editableCapacity,
      });
      onUpdate();
      setIsDirty(false);
      toast({
        title: "Slot updated",
        description: "The slot has been updated successfully",
      });
    } catch (error) {
      console.error("Error updating slot:", error);
      toast({
        title: "Error",
        description: "Failed to update slot",
        variant: "destructive",
      });
    }
  };

  const handleCancelChanges = () => {
    setEditableName(slot.name || "");
    setEditableCapacity(slot.capacity);
    setIsDirty(false);
  };

  const handleDeleteSlot = async () => {
    try {
      setIsDeleting(true);
      await slotService.deleteSlot(slot.id);
      onUpdate();
      toast({
        title: "Slot deleted",
        description: "The slot has been deleted successfully",
      });
    } catch (error) {
      console.error("Error deleting slot:", error);
      toast({
        title: "Error",
        description: "Failed to delete slot",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleStatusChange = async (status: string) => {
    try {
      await slotService.updateSlot(slot.id, { status: status as any });
      onUpdate();
      toast({
        title: "Status updated",
        description: `The slot status has been changed to ${status}`,
      });
    } catch (error) {
      console.error("Error updating slot status:", error);
      toast({
        title: "Error",
        description: "Failed to update slot status",
        variant: "destructive",
      });
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "available":
        return (
          <Badge variant="outline" className="bg-good-bg text-good">
            Available
          </Badge>
        );
      case "full":
        return (
          <Badge variant="outline" className="bg-mid-bg text-mid">
            Full
          </Badge>
        );
      case "cancelled":
        return (
          <Badge variant="outline" className="bg-danger-bg text-danger">
            Cancelled
          </Badge>
        );
      case "archived":
        return (
          <Badge variant="outline" className="bg-muted text-foreground">
            Archived
          </Badge>
        );
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <div className="space-y-4">
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="details">Details</TabsTrigger>
          <TabsTrigger value="bookings">Bookings</TabsTrigger>
        </TabsList>

        <TabsContent value="details" className="space-y-4">
          <div className="flex justify-between items-start">
            <div className="space-y-2 flex-1 mr-4">
              <Input
                value={editableName}
                onChange={handleNameChange}
                placeholder="Enter slot name"
                className="text-lg font-semibold bg-transparent"
              />
              {segments.length > 0 && (
                <p className="text-sm text-muted-foreground">
                  {format(
                    parseISO(segments[0].start_time),
                    "EEEE, MMMM d, yyyy",
                  )}
                </p>
              )}
            </div>
            {getStatusBadge(slot.status)}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <h4 className="text-sm font-medium text-muted-foreground mb-2">
                Capacity
              </h4>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  value={editableCapacity}
                  onChange={handleCapacityChange}
                  min={slot.capacity}
                  className="w-24"
                />
                <span className="text-sm text-muted-foreground">
                  ({availableSeats} available)
                </span>
              </div>
            </div>

            {slot.time_limit && (
              <div>
                <h4 className="text-sm font-medium text-muted-foreground">
                  Time Limit
                </h4>
                <p>{slot.time_limit} minutes</p>
              </div>
            )}

            {slot.campaign_id && (
              <div>
                <h4 className="text-sm font-medium text-muted-foreground">
                  Campaign
                </h4>
                <p>Attached to campaign</p>
              </div>
            )}
          </div>

          {slot.description && (
            <div>
              <h4 className="text-sm font-medium text-muted-foreground">
                Description
              </h4>
              <p className="mt-1">{slot.description}</p>
            </div>
          )}

          <div className="mt-4">
            <h4 className="text-sm font-medium text-muted-foreground mb-2">
              Slot Segments
            </h4>
            {segments.length > 0 ? (
              <div className="space-y-2">
                {segments.map((segment, index) => {
                  const startTime = parseISO(segment.start_time);
                  const endTime = parseISO(segment.end_time);
                  return (
                    <Card
                      key={segment.id || index}
                      className="border-l-4"
                      style={{
                        borderLeftColor: slot.color
                          ? `var(--${slot.color}-500)`
                          : "var(--primary)",
                      }}
                    >
                      <CardContent className="p-3">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">
                            {format(startTime, "EEEE, MMMM d, yyyy")}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <Clock className="h-4 w-4 text-muted-foreground" />
                          <span>
                            {format(startTime, "h:mm a")} -{" "}
                            {format(endTime, "h:mm a")}
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            ) : (
              <p className="text-muted-foreground">No slot segments defined</p>
            )}
          </div>

          <div className="space-y-4 mt-4">
            <div>
              <h4 className="text-sm font-medium text-muted-foreground mb-2">
                Booking URL
              </h4>
              <div className="flex">
                <Input
                  value={`${window.location.origin}/book-slot/${slot.id}`}
                  readOnly
                  className="rounded-r-none"
                />
                <Button
                  className="rounded-l-none"
                  onClick={() => {
                    const url = `${window.location.origin}/book-slot/${slot.id}`;
                    navigator.clipboard.writeText(url);
                    toast({
                      title: "Copied!",
                      description: "Booking URL copied to clipboard",
                    });
                  }}
                >
                  Copy
                </Button>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              {isDirty ? (
                <>
                  <Button onClick={handleSave}>Save Changes</Button>
                  <Button variant="outline" onClick={handleCancelChanges}>
                    Cancel Changes
                  </Button>
                </>
              ) : null}

              {slot.status === "available" && (
                <Button
                  variant="outline"
                  onClick={() => handleStatusChange("cancelled")}
                >
                  Cancel Slot
                </Button>
              )}

              {slot.status === "cancelled" && (
                <Button
                  variant="outline"
                  onClick={() => handleStatusChange("available")}
                >
                  Reactivate
                </Button>
              )}

              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive">Delete</Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This action cannot be undone. This will permanently delete
                      the slot and all associated bookings.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleDeleteSlot}
                      className="bg-destructive text-destructive-foreground"
                      disabled={isDeleting}
                    >
                      {isDeleting ? "Deleting..." : "Delete"}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="bookings">
          <SlotBookingsList slotId={slot.id} onUpdate={onUpdate} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
